import { typeHistogram, roundWeight, roundLength, getWeightUnit, getLengthUnit } from '../../config/commons';

import { Analysis, Bins, Dataset, Frequency } from './interfaces';

export const limitsPigmentation = [0.0, 0.2, 0.4, 0.6, 0.8, 1.0];
export const limitsConditionFactor = [0.4, 0.6, 0.8, 1.0, 1.2, 1.4, 1.6];
export const colosPigmentation = ['#E2C066', '#CDA830', '#A27F1D', '#6A5122', '#3B320E'];
export const IDEAL_CONDITION_FACTOR = 1;

export function getDataset (props: {frequencyData?: Frequency; animals?: number}): Dataset[] {
  const { frequencyData, animals } = props;
  const dataset: Dataset[] = [];

  if (!frequencyData) {
    return dataset;
  }

  for (let index = 0; index < frequencyData.frequencies.length; index++) {
    const value: number = frequencyData.frequencies[index];

    const data: Dataset = { frequency: value, index };

    if (animals) {
      const percent = value * 100 / animals;
      data.percent = percent;
    }

    dataset.push(data);
  }
  return dataset;
}

export function getFrequency (histogram: string, analysisData: Analysis, numberBins: number) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
    default: {
      const frequencyData: Frequency = getFrequencyData({ histogramBins: analysisData.resultData.histogramBins, numberBins });
      const originalFrequencyData: Frequency = getFrequencyData({ histogramBins: analysisData.originalResultData?.histogramBins, numberBins });

      return {
        frequencyData,
        originalFrequencyData,
      };
    }

    case typeHistogram.LENGTH: {
      const frequencyData: Frequency = getFrequencyData({ histogramBins: analysisData.resultData.histogramLengthBins, numberBins });
      const originalFrequencyData: Frequency = getFrequencyData({ histogramBins: analysisData.originalResultData?.histogramLengthBins, numberBins });

      return {
        frequencyData,
        originalFrequencyData,
      };
    }
      
    case typeHistogram.PIGMENTATION: {
      const frequencies: number[] = analysisData.resultData.histogramPigmentation;
      const frequencyData: Frequency = { frequencies: frequencies, limits: limitsPigmentation };
      
      return {
        frequencyData
      };
    }

    case typeHistogram.CONDITION_FACTOR: {
      const frequencies: number[] = analysisData.resultData.histogramConditionFactor;
      const frequencyData: Frequency = { frequencies: frequencies, limits: limitsConditionFactor };

      return {
        frequencyData
      };
    }
  }
}

export function getAverageOriginal (histogram: string, analysisData: Analysis) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
      return analysisData.resultData.averageWeight;

    case typeHistogram.LENGTH:
      return analysisData.resultData.averageLength;

    case typeHistogram.PIGMENTATION:
      return 0.6;

    default:
      return analysisData.resultData.averageWeight;
  }
}

export function getAnimalsAboveAverage (histogram: string, analysisData: Analysis) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
    default:
      return analysisData.resultData.animalsAboveAverageWeight;

    case typeHistogram.LENGTH:
      return analysisData.resultData.animalsAboveAverageLength;

    case typeHistogram.CONDITION_FACTOR:
      return analysisData.resultData.animalsAboveConditionFactor;
  }
}

export function getAnimalsBelowAverage (histogram: string, analysisData: Analysis) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
    default:
      return analysisData.resultData.animalsBelowAverageWeight;

    case typeHistogram.LENGTH:
      return analysisData.resultData.animalsBelowAverageLength;

    case typeHistogram.CONDITION_FACTOR:
      return analysisData.resultData.animalsBelowConditionFactor;
  }
}

export function getPercentageAnimalsAboveAverage (props: { total: number; animalsAboveAverage: number }) {
  const { total, animalsAboveAverage } = props;
  return animalsAboveAverage * 100 / total;
}

export function getAverage (histogram: string, analysisData: Analysis) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
    default:
      return roundWeight({ value: analysisData.resultData.averageWeight, showUnit: false });

    case typeHistogram.LENGTH:
      return roundLength({ value: analysisData.resultData.averageLength, showUnit: false });

    case typeHistogram.PIGMENTATION:
      return 0.6;

    case typeHistogram.CONDITION_FACTOR:
      return IDEAL_CONDITION_FACTOR;
  }
}

export function getUnit (histogram: string, analysisData: Analysis) {
  switch (histogram) {
    case typeHistogram.WEIGHT:
    default:
      return getWeightUnit(analysisData.resultData.averageWeight);

    case typeHistogram.LENGTH:
      return getLengthUnit(analysisData.resultData.averageLength);

    case typeHistogram.CONDITION_FACTOR:
      return '';
  }
}

export function getLimits (histogram: string, frequency: Frequency, averageOriginal: number) {
  let limits: number[] = [];

  switch (histogram) {
    case typeHistogram.WEIGHT:
      limits = roundWeight({ value: frequency.limits, showUnit: false, average: averageOriginal }) as number[];
      break;

    case typeHistogram.LENGTH:
      limits = roundLength({ value: frequency.limits, showUnit: false, average: averageOriginal }) as number[];
      break;

    default:
      limits = frequency.limits;
      break;
  }

  return limits;
}

export function getFrequencyData (props: {histogramBins?: Bins, numberBins: number}) {
  const { histogramBins, numberBins } = props;

  if (!histogramBins) {
    return { frequencies: [], limits: [] };
  }

  const frequencySelected = histogramBins[numberBins as keyof typeof histogramBins];

  if (numberBins >= 6 && numberBins <= 10 && frequencySelected) {
    return frequencySelected;
  }

  return { frequencies: [], limits: [] };
}
